import React, { useCallback } from 'react';
import jsPDF from 'jspdf';
import type { jsPDF as JSPDFType } from 'jspdf';
import html2canvas from 'html2canvas';
// ReactDOM import removed - using dynamic import for createRoot
import { ItineraryData } from '@/lib/builder/models/itinerary.model';
import { Tables } from '@/integrations/supabase/types';
import { TravelRequest } from '@/lib/request-trip/models/travel-request.model';
import { getActivityTypeLabel } from '@/lib/builder/models/activity-configs.model';
import {
  Bed,
  Calendar,
  Plane,
  FileText,
  FerrisWheel,
  CarTaxiFront,
  MicVocal,
  ClockArrowDown,
  UtensilsCrossed
} from "lucide-react";
import { User } from '@supabase/supabase-js';

// PDF Configuration Constants
const PDF_CONFIG = {
  // Fonts (using jsPDF built-in fonts)
  FONT_FAMILY: 'helvetica',
  FONT_WEIGHT: {
    NORMAL: 'normal',
    BOLD: 'bold'
  },

  // Colors (RGB values for jsPDF)
  COLORS: {
    BLACK: [0, 0, 0],
    WHITE: [255, 255, 255],
    GRAY_LIGHT: [248, 249, 250], // Very light gray for backgrounds
    GRAY_MEDIUM: [108, 117, 125], // Medium gray for secondary text
    GRAY_DARK: [52, 58, 64], // Dark gray for borders
    GRAY_BORDER: [233, 236, 239], // Light border color
    BRAND_PRIMARY: [3, 10, 20], // Place2Be brand color #030A14
    SUCCESS: [25, 135, 84], // Green for positive elements
    WARNING: [255, 193, 7], // Amber for highlights
    GRAY_INFO: [242, 242, 242], // Light gray for informational text
    GRAY_VALUE: [106, 106, 106], // Gray for labels
  },

  // Font Sizes
  FONT_SIZES: {
    TITLE: 24,
    SECTION_HEADER: 16,
    SUB_SECTION_HEADER: 14,
    ACTIVITY_TITLE: 12,
    BODY_TEXT: 10,
    SMALL_TEXT: 9,
    FOOTER_TEXT: 8,
    LOGO_TEXT: 16,
  },

  // Spacing and Layout
  SPACING: {
    MARGIN: 20,
    LINE_HEIGHT: 7,
    NO_LINE_HEIGHT: 4,
    SECTION_SPACING: 15,
    HEADER_SPACE: 25,
    FOOTER_SPACE: 25,
    ACTIVITY_INDENT: 5,
    TYPE_INDENT: 10,
  },

  // Logo Configuration
  LOGO: {
    WIDTH: 40,
    HEIGHT: 8,
    CANVAS_WIDTH: 480, // 2x original for high quality
    CANVAS_HEIGHT: 98, // 2x original for high quality
    URL: '/assets/images/dark_extended.svg',
  },

  LOGO_SMALL: {
    WIDTH: 8,
    HEIGHT: 8,
    CANVAS_WIDTH: 20, // 2x original for high quality
    CANVAS_HEIGHT: 20, // 2x original for high quality
    URL: '/assets/images/dark_small.svg',
  },

  IMAGES: {
    MAX_WIDTH: 210, // Max image width in mm
    MAX_HEIGHT: 40, // Max image height in mm
  },

  HEADER: {
    HEIGHT: 12,
  },

  FOOTER: {
    HEIGHT: 20,
  },

  // Icon Configuration
  ICONS: {
    SIZE: 4, // Icon size in mm (reduced from 12)
    CANVAS_SIZE: 16, // Canvas size for high quality (reduced from 24)
    COLOR: [0, 0, 0], // Black color for icons
  },

  // Page Configuration
  PAGE: {
    WIDTH: 210, // A4 width in mm
    HEIGHT: 297, // A4 height in mm
  }
} as const;

// Icon cache to avoid regenerating the same icons
const iconCache = new Map<string, string>();


// Utility function to clean text for jsPDF compatibility
const cleanTextForPDF = (text: string): string => {
  if (!text) return '';

  // Character mapping for common special characters
  const charMap: Record<string, string> = {
    // Accented characters
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a', 'å': 'a', 'æ': 'ae',
    'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
    'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
    'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'o', 'ø': 'o', 'œ': 'oe',
    'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
    'ý': 'y', 'ÿ': 'y',
    'ñ': 'n', 'ç': 'c',
    // Uppercase
    'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A', 'Å': 'A', 'Æ': 'AE',
    'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
    'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
    'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O', 'Ö': 'O', 'Ø': 'O', 'Œ': 'OE',
    'Ù': 'U', 'Ú': 'U', 'Û': 'U', 'Ü': 'U',
    'Ý': 'Y', 'Ÿ': 'Y',
    'Ñ': 'N', 'Ç': 'C',
    // Special symbols
    '→': '->', '←': '<-', '↑': '^', '↓': 'v',
    '°': 'deg', '€': 'EUR', '£': 'GBP', '¥': 'JPY',
    '©': '(c)', '®': '(R)', '™': '(TM)',
    '…': '...', '–': '-', '—': '-',
    '«': '<<', '»': '>>', '‹': '<', '›': '>',
    '¿': '?', '¡': '!',
    // Other common characters
    'ß': 'ss', 'ð': 'd', 'þ': 'th'
  };

  return text
    .split('')
    .map(char => charMap[char] || (char.charCodeAt(0) < 128 ? char : ''))
    .join('')
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

// Icon mapping to Lucide React components
const getIconComponent = (type: string) => {
  const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    'accommodation': Bed,
    'accommodationIn': Bed,
    'accommodationOut': ClockArrowDown,
    'activity': FerrisWheel,
    'flight': Plane,
    'transportation': CarTaxiFront,
    'food-drinks': UtensilsCrossed,
    'events': MicVocal,
    'notes': FileText
  };

  return iconMap[type] || Calendar; // Calendar as fallback
};

interface ItineraryPDFData {
  itinerary: Tables<'itineraries'>;
  activities: Tables<'itinerary_activities'>[];
  travelRequest?: Tables<'travel_requests'> | TravelRequest; // Support both Supabase type and custom model
  travelDesigner?: Tables<'users'> | User; // Support both Supabase type and custom model
}

export const useItineraryPDF = () => {
  const generatePDF = useCallback(async (itineraryId: string, instantDownload: boolean): Promise<{ pdf: JSPDFType, fileName: string }> => {
    try {
      // Fetch itinerary data
      const { supabase } = await import('@/integrations/supabase/client');

      // Fetch itinerary
      const { data: itinerary, error: itineraryError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', itineraryId)
        .single();

      if (itineraryError || !itinerary) {
        throw new Error('Itinerary not found');
      }

      // Fetch activities
      const { data: activities, error: activitiesError } = await supabase
        .from('itinerary_activities')
        .select('*')
        .eq('itinerary_id', itineraryId)
        .order('date', { ascending: true });

      if (activitiesError) {
        throw new Error('Failed to fetch activities');
      }

      // Fetch travel request
      const { data: travelRequest } = await supabase
        .from('travel_requests')
        .select('*')
        .eq('id', itinerary.travel_request_id)
        .single();

      const { data: travelDesigner } = await supabase
        .from('users')
        .select('*')
        .eq('id', travelRequest?.assigned_designer_id)
        .single();

      const pdfData: ItineraryPDFData = {
        itinerary,
        activities: activities || [],
        travelRequest,
        travelDesigner
      };

      // Generate PDF
      const pdfEntity = await createItineraryPDF(pdfData);
      if (instantDownload) {
        pdfEntity.pdf.save(pdfEntity.fileName);
      } else {
        return pdfEntity;
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }, []);

  const generatePDFFromData = useCallback(async (itineraryData: ItineraryData) => {
    try {
      // Create a mock itinerary object with required fields
      const mockItinerary = {
        ...itineraryData.getPayload(),
        id: itineraryData.id || 'temp-id',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Convert DayActivity[] to Tables<'itinerary_activities'>[] format
      const mockActivities = itineraryData.itinerary_days?.flatMap(day =>
        day.activities.map(activity => {
          // Helper function to safely get property values
          const getProperty = (obj: unknown, key: string, defaultValue: unknown = '') => {
            return obj && typeof obj === 'object' && key in obj ? (obj as Record<string, unknown>)[key] : defaultValue;
          };

          return {
            id: activity.id,
            itinerary_id: itineraryData.id || 'temp-id',
            title: activity.title,
            description: activity.description,
            location: activity.location,
            type: activity.type,
            date: activity.date,
            include_price: activity.include_price,
            link: getProperty(activity, 'link') as string || '',
            address: getProperty(activity, 'address') as string || '',
            start_date: getProperty(activity, 'start_date') as string || '',
            end_date: getProperty(activity, 'end_date') as string || '',
            start_time: getProperty(activity, 'start_time') as string || '',
            end_time: getProperty(activity, 'end_time') as string || '',
            cost: getProperty(activity, 'cost', 0) as number,
            check_in_time: getProperty(activity, 'check_in_time') as string || '',
            check_out_time: getProperty(activity, 'check_out_time') as string || '',
            check_in_note: getProperty(activity, 'check_in_note') as string || '',
            refundable_rate: getProperty(activity, 'refundable_rate', false) as boolean,
            price_tag: getProperty(activity, 'price_tag') as string || '',
            transport_type: getProperty(activity, 'transport_type') as string || '',
            company: getProperty(activity, 'company') as string || '',
            media: getProperty(activity, 'media', []) as unknown[],
            documentation: getProperty(activity, 'documentation', []) as unknown[],
            travels: getProperty(activity, 'travels', []) as unknown[],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        })
      ) || [];

      const pdfData: ItineraryPDFData = {
        itinerary: mockItinerary,
        activities: mockActivities,
        travelRequest: itineraryData.travel_request
      };

      const pdfEntity = await createItineraryPDF(pdfData);
      pdfEntity.pdf.save(pdfEntity.fileName);
    } catch (error) {
      console.error('Error generating PDF from data:', error);
      throw error;
    }
  }, []);

  return {
    generatePDF,
    generatePDFFromData
  };
};

const createItineraryPDF = async (data: ItineraryPDFData): Promise<{ pdf: JSPDFType, fileName: string }> => {
  const { itinerary, activities, travelRequest, travelDesigner } = data;

  // Pre-load all required icons to improve performance
  const uniqueActivityTypes = [...new Set(activities.map(activity => activity.type))];
  await Promise.all(uniqueActivityTypes.map(type => createIconBase64(type)));

  // Create new PDF document
  const pdf = new jsPDF('p', 'mm', 'a4');

  // Add Montserrat font (fallback to default if not available)
  try {
    // Try to use Montserrat if available, otherwise fallback to default
    pdf.setFont('helvetica', 'normal'); // Use helvetica as fallback
  } catch (error) {
    console.warn('Montserrat font not available, using default font');
  }
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = PDF_CONFIG.SPACING.MARGIN;
  const contentWidth = pageWidth - (margin * 2);

  const lineHeight = PDF_CONFIG.SPACING.LINE_HEIGHT;
  const sectionSpacing = PDF_CONFIG.SPACING.SECTION_SPACING;

  // Pure helper function to check page break and return new position
  const checkPageBreak = async (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number): Promise<number> => {
    const footerSpace = PDF_CONFIG.SPACING.FOOTER_SPACE;
    if (currentY + requiredSpace > pageHeight - margin - footerSpace) {
      addFooter(pdf, pageWidth, pageHeight, 10);
      pdf.addPage();
      // new pages: secondary header (isFirstPage = false)
      const headerHeight = await addHeader(pdf, pageWidth, margin, itinerary, false);
      return margin + headerHeight;
    }
    return currentY;
  };

  // Add header to first page (now returns used height) - first page keeps isFirstPage = true
  const initialHeaderHeight = await addHeader(pdf, pageWidth, margin, itinerary, true);
  let currentY = margin + initialHeaderHeight;

  // Create PDF sections - each function returns the new Y position
  currentY = await createTitleSection(pdf, itinerary, margin, currentY, lineHeight, pageHeight, pageWidth, checkPageBreak);
  currentY = await createTripInformationSection(pdf, itinerary, margin, currentY, lineHeight, contentWidth, pageHeight, pageWidth, checkPageBreak);
  currentY = await createDescriptionSection(pdf, itinerary, margin, currentY, lineHeight, contentWidth, sectionSpacing, pageHeight, pageWidth, checkPageBreak);
  await addFirstPageFooter(pdf, pageWidth, pageHeight, margin, travelDesigner);

  pdf.addPage();
  currentY = margin + initialHeaderHeight;
  const headerHeightAfterFirst = await addHeader(pdf, pageWidth, margin, itinerary, false);
  currentY = margin + headerHeightAfterFirst;

  currentY = await createDailyItinerarySection(pdf, activities, margin, currentY, lineHeight, contentWidth, sectionSpacing, pageHeight, pageWidth, checkPageBreak);
  currentY = await createTotalSection(pdf, itinerary, activities, margin, currentY, lineHeight, contentWidth, pageWidth, pageHeight, checkPageBreak);
  // Footer

  // Save PDF
  const fileName = `itinerary-${itinerary.title.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
  return { pdf, fileName };
};

const addFirstPageFooter = async (pdf: jsPDF, pageWidth: number, pageHeight: number, margin: number, travelDesigner?: Tables<'users'> | User | undefined): Promise<void> => {
  try {
    const footerY = pageHeight - margin - PDF_CONFIG.FOOTER.HEIGHT;

    // Separator line
    pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_BORDER);
    pdf.setLineWidth(0.4);
    pdf.line(margin, footerY - 4, pageWidth - margin, footerY - 4);

    // Cell positions
    const contentWidth = pageWidth - margin * 2;
    const halfWidth = contentWidth / 2;
    const leftX = margin + 2;
    const rightX = margin + halfWidth - 2;
    const rightAlignX = pageWidth - margin - 2;
    const startY = footerY + 8; // baseline for first line

    // Extract designer info
    const designer = travelDesigner ?? ({} as any);
    const fullName = (designer.full_name || `${designer.first_name || ''} ${designer.last_name || ''}` || designer.name || '').toString().trim();
    const phone = (designer.phone || '').toString().trim();
    const email = (designer.email || '').toString().trim();

    // Left cell: Name (bold black) then "Travel designer" (gray)
    pdf.setFontSize(12);
    if (fullName) {
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      pdf.text(cleanTextForPDF(fullName), leftX, startY);
    } else {
      // if no name, still reserve space (optional)
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      pdf.text('', leftX, startY);
    }

    pdf.setFontSize(12);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    pdf.setTextColor(0x6A, 0x6A, 0x6A); // #6A6A6A
    pdf.text('Travel designer', leftX, startY + PDF_CONFIG.SPACING.LINE_HEIGHT);

    // Right cell: phone (bold) then email then website
    let rightLineY = startY;
    if (phone) {
      pdf.setFontSize(12);
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      // right-aligned
      pdf.text(cleanTextForPDF(phone), rightAlignX, rightLineY, { align: 'right' });
      rightLineY += PDF_CONFIG.SPACING.LINE_HEIGHT;
    }

    if (email) {
      pdf.setFontSize(12);
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      pdf.text(cleanTextForPDF(email), rightAlignX, rightLineY, { align: 'right' });
      rightLineY += PDF_CONFIG.SPACING.LINE_HEIGHT;
    }

    // Website (always present)
    pdf.setFontSize(12);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_MEDIUM);
    pdf.text('www.place2be.it', rightAlignX, rightLineY, { align: 'right' });

    // Optional avatar on the far right (if available) - keep previous behaviour
    const avatarUrl = travelDesigner ? ((travelDesigner as any).avatar_url || (travelDesigner as any).avatar || '') : '';
    if (avatarUrl) {
      try {
        const imgData = await loadImageUrlAsBase64(avatarUrl);
        if (imgData && imgData.base64) {
          const avatarSize = 14;
          const avatarX = pageWidth - margin - avatarSize;
          const avatarY = footerY + 4;
          pdf.addImage(imgData.base64, 'PNG', avatarX, avatarY, avatarSize, avatarSize);
        }
      } catch (err) {
        // ignore avatar errors
      }
    }

  } catch (err) {
    // don't block PDF generation for footer errors
    console.warn('Could not draw first page footer', err);
  }
};
// Section creation functions
const createTitleSection = async (pdf: jsPDF, itinerary: Tables<'itineraries'>, margin: number, currentY: number, lineHeight: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  // Title with professional styling
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.TITLE);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BRAND_PRIMARY);
  pdf.text(cleanTextForPDF(itinerary.title), margin, currentY);
  return currentY + lineHeight * 1.5;
};

const createTripInformationSection = async (pdf: jsPDF, itinerary: Tables<'itineraries'>, margin: number, currentY: number, lineHeight: number, contentWidth: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  // Layout and styles for the four columns
  const keyFontSize = 12;
  const valueFontSize = 12;
  const valueColor = [0, 0, 0];

  // Prepare values
  const startDate = itinerary.start_date ? new Date(itinerary.start_date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  }) : '';
  const endDate = itinerary.end_date ? new Date(itinerary.end_date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  }) : '';

  const guestsData = Array.isArray(itinerary.guests_data) ? itinerary.guests_data : [];
  const guestsText = `${guestsData.length}`;

  const numberOfGuests = Array.isArray(itinerary.guests_data) ? itinerary.guests_data.length : 1;
  const totalForPerson = (itinerary.total / numberOfGuests).toFixed(2);
  const budgetText = `€ ${totalForPerson.toLocaleString()}`;

  const columns = [
    { key: 'From', value: cleanTextForPDF(startDate) },
    { key: 'To', value: cleanTextForPDF(endDate) },
    { key: 'Guests', value: cleanTextForPDF(guestsText) },
    { key: 'Budget per person', value: budgetText } // EUR symbol kept
  ];

  const colCount = columns.length;
  const colWidth = contentWidth / colCount;
  const rowX = margin;
  const rowY = currentY;

  // For each column, render key (grigio) e value (bold, nero) su riga successiva con wrapping per il valore
  const valueLinesPerCol = columns.map(col => {
    // Use available width inside column
    const lines = pdf.splitTextToSize(col.value || '', colWidth + 10); // padding per non andare a capo con le date
    return lines;
  });

  // Compute max lines (1 line for key + 1 lines for value)
  const maxLines = 2;

  for (let i = 0; i < colCount; i++) {
    const col = columns[i];
    const x = rowX + (i * colWidth);

    // Key (small/regular, gray)
    pdf.setFontSize(keyFontSize);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_VALUE);
    pdf.text(cleanTextForPDF(col.key), x, rowY);

    // Value (bold, black) - allow wrapping
    pdf.setFontSize(valueFontSize);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
    pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
    const vLines = valueLinesPerCol[i].length ? valueLinesPerCol[i] : [''];
    // Value start Y is one line below key
    const valueStartY = rowY + lineHeight;
    pdf.text(vLines, x, valueStartY);
  }

  // Advance Y by the tallest column content (key + value lines)
  currentY = rowY + (maxLines * lineHeight);

  // Small spacing after the row
  return currentY + 8;
};

const createDescriptionSection = async (pdf: jsPDF, itinerary: Tables<'itineraries'>, margin: number, currentY: number, lineHeight: number, contentWidth: number, sectionSpacing: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  if (!itinerary.description) return currentY;

  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SUB_SECTION_HEADER);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  const cleanDescription = cleanTextForPDF(itinerary.description);
  const descriptionLines = pdf.splitTextToSize(cleanDescription, contentWidth);
  pdf.text(descriptionLines, margin, currentY);
  return currentY + descriptionLines.length * lineHeight + sectionSpacing;
};

const createDailyItinerarySection = async (pdf: jsPDF, activities: Tables<'itinerary_activities'>[], margin: number, currentY: number, lineHeight: number, contentWidth: number, sectionSpacing: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  // Group activities by date
  const activitiesByDate = activities.reduce((acc, activity) => {
    const date = activity.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(activity);
    return acc;
  }, {} as Record<string, Tables<'itinerary_activities'>[]>);

  // Sort dates
  const sortedDates = Object.keys(activitiesByDate).sort();

  // Daily Itinerary header
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SECTION_HEADER);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.text('Daily Itinerary', margin, currentY);
  currentY += lineHeight * 2;

  for (const date of sortedDates) {
    const dayActivities = activitiesByDate[date];
    const dayDate = new Date(date);
    const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'long' });
    const dayNumber = dayDate.getDate();
    const monthName = dayDate.toLocaleDateString('en-US', { month: 'long' });

    // Day header
    const estimatedFirstActivityHeight = calculateContentHeight(pdf, dayActivities[0], contentWidth, lineHeight);
    currentY = await checkPageBreak(pdf, currentY, estimatedFirstActivityHeight + PDF_CONFIG.HEADER.HEIGHT, pageHeight, margin, pageWidth);
    currentY = await createDayHeader(pdf, dayName, dayNumber, monthName, margin, currentY, contentWidth);

    // Activities for this day
    for (const activity of dayActivities) {
      const estimatedActivityHeight = calculateContentHeight(pdf, activity, contentWidth, lineHeight);
      currentY = await checkPageBreak(pdf, currentY, estimatedActivityHeight, pageHeight, margin, pageWidth);
      currentY = await createActivityCard(pdf, activity, margin, currentY, lineHeight, contentWidth);
    }

    currentY += sectionSpacing;
  }

  return currentY;
};

const createTotalSection = async (
  pdf: jsPDF,
  itinerary: Tables<'itineraries'>,
  activities: Tables<'itinerary_activities'>[],
  margin: number,
  currentY: number,
  lineHeight: number,
  contentWidth: number,
  pageWidth: number,
  pageHeight: number,
  checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>
): Promise<number> => {
  // Reserve space for three text lines (key, big total, helper message)
  const approxHeight = (8) + (10) + (10) + 12; // safe mm estimate
  currentY = await checkPageBreak(pdf, currentY, approxHeight, pageHeight, margin, pageWidth);

  const totalCost = itinerary.total || 0;
  const leftX = margin;

  // "Total:" label - gray
  pdf.setFontSize(12);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_VALUE);
  pdf.text('Total: ', leftX, currentY);

  // Total amount on next line - black, 16px (use 16 as fontSize)
  const totalY = currentY + 8; // spacing between lines
  pdf.setFontSize(16);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  pdf.text(`€ ${Number(totalCost).toLocaleString()}`, leftX, totalY);

  // Helper message on next line - gray, 10px
  const msgY = totalY + 10;
  pdf.setFontSize(10);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_VALUE);
  pdf.text('Check your email to complete the payment using the method you selected.', leftX, msgY);

  // Return new Y position with small bottom spacing
  return msgY + 8;
};

const createDayHeader = async (pdf: jsPDF, dayName: string, dayNumber: number, monthName: string, margin: number, currentY: number, contentWidth: number): Promise<number> => {
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SUB_SECTION_HEADER);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);

  const headerY = currentY - 5;

  // Main background
  pdf.setFillColor(...PDF_CONFIG.COLORS.GRAY_INFO);
  pdf.rect(margin, headerY, contentWidth, PDF_CONFIG.HEADER.HEIGHT, 'F');

  // Center text vertically in the header block
  const textY = headerY + (PDF_CONFIG.HEADER.HEIGHT / 2) + 2;
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  pdf.text(`${monthName} ${dayNumber} - ${dayName}`, margin + 8, textY);

  // Reset text color
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  return headerY + PDF_CONFIG.HEADER.HEIGHT + 8; // End of header + 8px spacing
};

const calculateContentHeight = (pdf: jsPDF, activity: Tables<'itinerary_activities'>, contentWidth: number, lineHeight: number): number => {
  let totalHeight = 0;
  const lineSpacing = 1; // Extra spacing between lines
  
  // Helper function to calculate label width
  const getLabelWidth = (label: string): number => {
    const currentSize = pdf.getFontSize();
    pdf.setFontSize(PDF_CONFIG.FONT_SIZES.BODY_TEXT);
    const width = pdf.getTextWidth(label);
    pdf.setFontSize(currentSize);
    return width;
  };

  // Type badge height (first row)
  totalHeight += lineHeight + lineSpacing;

  // Title height (second row)
  totalHeight += lineHeight;

  // Location height
  if (activity.location) {
    totalHeight += lineHeight + lineSpacing;
  }

  // Type-specific details height
  switch (activity.type) {
    case 'accommodation':
    case 'accommodationIn':
    case 'accommodationOut':
      if (activity.start_date && activity.end_date) totalHeight += lineHeight * 2 + lineSpacing;
      if (activity.check_in_time && activity.check_out_time) totalHeight += lineHeight * 2 + lineSpacing;
      if (activity.address) totalHeight += lineHeight + lineSpacing;
      if (activity.check_in_note) totalHeight += lineHeight + lineSpacing;
      break;
    case 'activity':
    case 'events':
      if (activity.start_date && activity.end_date) totalHeight += lineHeight + lineSpacing;
      if (activity.start_time && activity.end_time) totalHeight += lineHeight + lineSpacing;
      if (activity.address) totalHeight += lineHeight + lineSpacing;
      break;
    case 'flight':
      if (activity.travels && Array.isArray(activity.travels)) {
        const travelsArray = activity.travels as unknown[];
        travelsArray.forEach((travel, index) => {
          const travelData = travel as { flightNumber?: string; airline?: string; from?: string; to?: string; fromDate?: string; fromTime?: string; toDate?: string; toTime?: string };
          let flightFields = 0;
          if (travelData.flightNumber) {
            const flightNumberText = cleanTextForPDF(travelData.flightNumber);
            flightFields += flightNumberText.includes(' -> ') ? 2 : 1;
          }
          if (travelData.airline) flightFields++;
          if (travelData.from && travelData.to) flightFields += 2;
          if (travelData.fromDate && travelData.fromTime) flightFields++;
          if (travelData.toDate && travelData.toTime) flightFields++;
          totalHeight += flightFields * (lineHeight + lineSpacing);
        });
      }
      break;
    case 'transportation':
      if (activity.transport_type) totalHeight += lineHeight + lineSpacing;
      if (activity.company) totalHeight += lineHeight + lineSpacing;
      if (activity.travels && Array.isArray(activity.travels)) {
        const travelsArray = activity.travels as unknown[];
        totalHeight += travelsArray.length * ((lineHeight + lineSpacing) * 2); // Each travel has from/to
      }
      break;
    case 'food-drinks':
    case 'notes':
      if (activity.link) {
        // Calculate wrapped link lines
        const linkText = cleanTextForPDF(activity.link);
        const labelWidth = getLabelWidth('Link: ');
        const availableWidth = Math.max(0, contentWidth - 16 - labelWidth);
        const linkLines = pdf.splitTextToSize(linkText, availableWidth);
        totalHeight += (linkLines.length * lineHeight) + lineSpacing;
      }
      break;
  }

  // Description height (with text wrapping) - use cleaned text and same width used when rendering
  if (activity.description) {
    // tolgo il campo descrizione per flight e transportation perchè è nascosto al cliente
    if (!(activity.type === 'flight' || activity.type === 'transportation')) {
      const cleanDesc = cleanTextForPDF(activity.description);
      const descriptionLines = pdf.splitTextToSize(cleanDesc, Math.max(0, contentWidth - 16)); // Account for padding
      totalHeight += descriptionLines.length * lineHeight + lineSpacing;
    }
  }

  // Count image heights
  if (activity.media && Array.isArray(activity.media) && activity.media.length > 0) {
    totalHeight += PDF_CONFIG.IMAGES.MAX_HEIGHT;
  }

  return totalHeight;
};

const createActivityCard = async (pdf: jsPDF, activity: Tables<'itinerary_activities'>, margin: number, currentY: number, lineHeight: number, contentWidth: number): Promise<number> => {
  // Calculate actual content height
  const contentHeight = calculateContentHeight(pdf, activity, contentWidth, lineHeight);

  // Add 16px padding (top + bottom) and reduce height by 50px
  const cardPadding = 16; // 8px top + 8px bottom
  const heightReduction = 11; // 50px ≈ 13mm in jsPDF
  const activityCardHeight = Math.max(contentHeight + cardPadding - heightReduction, 8); // Minimum height of 8mm
  const activityCardY = currentY - 3;
  const cardX = margin;
  const cardWidth = contentWidth;

  // Create rounded card background (simulated with multiple rectangles)
  pdf.setFillColor(...PDF_CONFIG.COLORS.WHITE);

  // Main card body
  pdf.rect(cardX + 2, activityCardY + 2, cardWidth - 4, activityCardHeight - 4, 'F');

  // Top rounded corners (simulated)
  pdf.rect(cardX + 1, activityCardY + 1, cardWidth - 2, 1, 'F');
  pdf.rect(cardX, activityCardY, cardWidth, 1, 'F');

  // Bottom rounded corners (simulated)
  pdf.rect(cardX + 1, activityCardY + activityCardHeight - 2, cardWidth - 2, 1, 'F');
  pdf.rect(cardX, activityCardY + activityCardHeight - 1, cardWidth, 1, 'F');

  // Card border (subtle)
  pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_BORDER);
  pdf.setLineWidth(0.3);
  pdf.rect(cardX, activityCardY, cardWidth, activityCardHeight, 'S');

  // Activity type label and icon
  const typeLabel = getActivityTypeLabel(activity.type as 'accommodation' | 'activity' | 'flight' | 'transportation' | 'food-drinks' | 'events' | 'notes');
  const typeIconBase64 = await createIconBase64(activity.type);

  // Add 4px padding to content positioning (left and right only)
  const contentPadding = 4;
  const topPadding = 8; // Additional top padding for better spacing
  const contentStartY = activityCardY + topPadding;
  let contentY = contentStartY;

  // Add icon image if available
  if (typeIconBase64) {
    try {
      const iconY = contentY - (PDF_CONFIG.ICONS.SIZE / 2) - 1;
      pdf.addImage(typeIconBase64, 'PNG', cardX + contentPadding + 2, iconY, PDF_CONFIG.ICONS.SIZE, PDF_CONFIG.ICONS.SIZE);
    } catch (error) {
      console.warn('Could not add icon image:', error);
    }
  }

  // Add activity type label in a left-aligned badge with rounded corners (first row)
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL_TEXT);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);

  // Create badge with dynamic width calculation
  const badgePadding = 3; // Internal padding
  const textWidth = pdf.getTextWidth(typeLabel); // Calculate actual text width
  const badgeWidth = textWidth + (badgePadding * 2); // Width with padding
  const badgeHeight = 6; // Slightly taller for better proportions
  const badgeX = typeIconBase64 ? cardX + contentPadding + 10 : cardX + contentPadding + 2;
  const badgeY = contentY - 4;

  // Badge border only (no background color)
  pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_BORDER);
  pdf.setLineWidth(0.2);
  pdf.rect(badgeX, badgeY, badgeWidth, badgeHeight, 'S');

  // Badge text with internal padding
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  pdf.text(typeLabel, badgeX + badgePadding, contentY);

  contentY += lineHeight * 1.2;

  // Add activity title on second row
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.ACTIVITY_TITLE);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BRAND_PRIMARY);
  const titleX = cardX + contentPadding + 2;

  // For flight activities, extract airport codes from title if it contains "->"
  let titleText = cleanTextForPDF(activity.title);
  if (activity.type === 'flight' && titleText.includes(' -> ')) {
    const [from, to] = titleText.split(' -> ');
    const fromMatch = from.match(/\(([^)]+)\)/);
    const toMatch = to.match(/\(([^)]+)\)/);
    const fromCode = fromMatch ? fromMatch[1] : from.split(' - ')[0].trim();
    const toCode = toMatch ? toMatch[1] : to.split(' - ')[0].trim();
    titleText = `From ${fromCode} to ${toCode}`;
  }

  pdf.text(titleText, titleX, contentY);
  contentY += lineHeight;

  // Activity details based on type
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.BODY_TEXT);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);

  // Location (common to all activities)
  if (activity.location) {
    pdf.text(`Location: ${cleanTextForPDF(activity.location)}`, cardX + contentPadding + 2, contentY);
    contentY += lineHeight;
  }

  // Type-specific information
  contentY = await addActivityDetails(pdf, activity, cardX + contentPadding + 2, contentY, lineHeight, contentWidth);

  // Description (common to all activities) - use wrapping
  if (activity.description && !(activity.type === 'flight' || activity.type === 'transportation')) {
    const cleanDesc = cleanTextForPDF(activity.description);
    const descAvailableWidth = Math.max(0, cardWidth - (contentPadding * 2) - 8);
    
    // Calculate width of "Description: " label
    const labelWidth = pdf.getTextWidth('Description: ');
    
    // Split text considering the label width on first line
    const firstLineWidth = descAvailableWidth - labelWidth;
    const descLines = pdf.splitTextToSize(cleanDesc, firstLineWidth);
    
    if (descLines.length > 0) {
      // Write label + first line
      pdf.text(`Description: ${descLines[0]}`, cardX + contentPadding + 2, contentY);
      contentY += PDF_CONFIG.SPACING.NO_LINE_HEIGHT;
      
      // Write remaining lines (if any) with full width
      if (descLines.length > 1) {
        const remainingLines = pdf.splitTextToSize(descLines.slice(1).join(' '), descAvailableWidth);
        pdf.text(remainingLines, cardX + contentPadding + 2, contentY);
        contentY += remainingLines.length * lineHeight;
      }
    } else {
      pdf.text('Description:', cardX + contentPadding + 2, contentY);
      contentY += lineHeight;
    }
  }

  if (activity.media && Array.isArray(activity.media) && activity.media.length > 0) {
    const mediaUrls = activity.media.filter(Boolean).slice(0, 4) as string[];
    if (mediaUrls.length > 0) {
      const gap = 3; // mm gap between image boxes
      const maxRowWidth = cardWidth - (contentPadding * 2);
      const imgCount = mediaUrls.length;
      const maxQuarterWidth = maxRowWidth / 4; // each image box max 1/4 of row
      // compute box width per image, but never exceed quarter width
      const boxWidth = Math.min((maxRowWidth - gap * (imgCount - 1)) / imgCount, maxQuarterWidth);
      const imgInnerPadding = (8 / 96) * 25.4; // 8px -> mm (≈2.1167mm)
      const imgAvailableWidth = Math.max(0, boxWidth - imgInnerPadding * 2);
      const pxToMm = (px: number) => (px / 96) * 25.4; // assume 96dpi

      let maxImgHeightUsed = 0;
      for (let i = 0; i < mediaUrls.length; i++) {
        const url = mediaUrls[i];
        try {
          // Use OPTIMIZED loader for activity images (reduces PDF size)
          const imgData = await loadActivityImageOptimized(url);
          if (!imgData || !imgData.base64) continue;

          // Preserve aspect ratio based on available width inside the box (after inner padding)
          const originalWidthMM = pxToMm(imgData.width || 1);
          const originalHeightMM = pxToMm(imgData.height || 1);
          const scale = originalWidthMM > 0 ? imgAvailableWidth / originalWidthMM : 1;
          let imgHeightMM = Math.max(0, originalHeightMM * scale);
          let imgWidthMM = imgAvailableWidth;

          // Cap image height to avoid very tall images inside card
          const maxImgHeightCap = PDF_CONFIG.IMAGES.MAX_HEIGHT; // mm
          if (imgHeightMM > maxImgHeightCap) {
            const capScale = maxImgHeightCap / imgHeightMM;
            imgHeightMM = maxImgHeightCap;
            imgWidthMM = imgWidthMM * capScale;
          }

          // Box position and image placement (centered inside box with inner padding)
          const boxX = cardX + contentPadding + i * (boxWidth + gap);
          const boxY = contentY;

          // If you want a visible image box, uncomment:
          // pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_BORDER);
          // pdf.rect(boxX, boxY, boxWidth, imgHeightMM + imgInnerPadding * 2, 'S');

          const imgX = boxX + imgInnerPadding + ((boxWidth - imgInnerPadding * 2) - imgWidthMM) / 2;
          const imgY = boxY + imgInnerPadding;

          pdf.addImage(imgData.base64, 'PNG', imgX, imgY, imgWidthMM, imgHeightMM);

          const usedHeight = imgInnerPadding + imgHeightMM + imgInnerPadding;
          if (usedHeight > maxImgHeightUsed) maxImgHeightUsed = usedHeight;
        } catch (err) {
          // ignore individual image errors
          console.warn('Could not render activity media image:', err);
        }
      }
    }
  }
  // Update currentY to the end of this card
  return activityCardY + activityCardHeight + 8; // spacing after card
};

const addActivityDetails = async (pdf: jsPDF, activity: Tables<'itinerary_activities'>, startX: number, contentY: number, lineHeight: number, contentWidth: number = 170): Promise<number> => {
  switch (activity.type) {
    case 'accommodation':
      // Print check-in/check-out dates including times when available
      if (activity.start_date && activity.end_date) {
        const checkInTime = activity.check_in_time ? ` - ${activity.check_in_time}` : '';
        const checkOutTime = activity.check_out_time ? ` - ${activity.check_out_time}` : '';
        pdf.text(`Check-in: ${activity.start_date}${checkInTime}`, startX, contentY);
        contentY += lineHeight;
        pdf.text(`Check-out: ${activity.end_date}${checkOutTime}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.address) {
        pdf.text(`Address: ${cleanTextForPDF(activity.address)}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.check_in_note) {
        pdf.text(`Notes: ${cleanTextForPDF(activity.check_in_note)}`, startX, contentY);
        contentY += lineHeight;
      }
      break;

    case 'activity':
    case 'events':
      if (activity.start_date && activity.end_date) {
        pdf.text(`Date: ${activity.start_date}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.start_time && activity.end_time) {
        pdf.text(`Time: ${activity.start_time} - ${activity.end_time}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.address) {
        pdf.text(`Address: ${cleanTextForPDF(activity.address)}`, startX, contentY);
        contentY += lineHeight;
      }
      break;

    case 'flight':
      if (activity.travels && Array.isArray(activity.travels) && activity.travels.length > 0) {
        const travelsArray = activity.travels as unknown[];
        travelsArray.forEach((travel, index: number) => {
          const travelData = travel as { flightNumber?: string; airline?: string; from?: string; to?: string; fromDate?: string; fromTime?: string; toDate?: string; toTime?: string };
          // Add flight number prefix for multiple flights
          const flightPrefix = travelsArray.length > 1 ? `Flight ${index + 1}: ` : '';

          if (travelData.flightNumber) {
            pdf.text(`${flightPrefix}${cleanTextForPDF(travelData.flightNumber)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.airline) {
            pdf.text(`Airline: ${cleanTextForPDF(travelData.airline)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.from && travelData.to) {
            pdf.text(`From: ${cleanTextForPDF(travelData.from)}`, startX, contentY);
            contentY += lineHeight;
            pdf.text(`To: ${cleanTextForPDF(travelData.to)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.fromDate && travelData.fromTime) {
            pdf.text(`Departure: ${cleanTextForPDF(travelData.fromDate)} at ${cleanTextForPDF(travelData.fromTime)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.toDate && travelData.toTime) {
            pdf.text(`Arrival: ${cleanTextForPDF(travelData.toDate)} at ${cleanTextForPDF(travelData.toTime)}`, startX, contentY);
            contentY += lineHeight;
          }

          // Add spacing between multiple flights
          if (index < travelsArray.length - 1) {
            contentY += lineHeight * 0.5; // Half line spacing between flights
          }
        });
      }
      break;

    case 'transportation':
      if (activity.transport_type) {
        pdf.text(`Transport: ${cleanTextForPDF(activity.transport_type.toUpperCase())}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.company) {
        pdf.text(`Company: ${cleanTextForPDF(activity.company)}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.travels && Array.isArray(activity.travels) && activity.travels.length > 0) {
        const travelsArray = activity.travels as unknown[];
        travelsArray.forEach((travel, index: number) => {
          const travelData = travel as { address?: string; date?: string; time?: string };
          if (travelData.address) {
            pdf.text(`Address: ${cleanTextForPDF(travelData.address)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.date && travelData.time) {
            pdf.text(`Date: ${cleanTextForPDF(travelData.date)} at ${cleanTextForPDF(travelData.time)}`, startX, contentY);
            contentY += lineHeight;
          }
        });
      }
      break;

    case 'food-drinks':
    case 'notes':
      if (activity.link) {
        const linkText = cleanTextForPDF(activity.link);
        // Calculate available width for the link after "Link: " label
        const labelWidth = pdf.getTextWidth('Link: ');
        const availableWidth = Math.max(0, (contentWidth || 170) - 16 - labelWidth);
        
        // Split link into multiple lines if needed
        const linkLines = pdf.splitTextToSize(linkText, availableWidth);
        
        // Render label and first line together
        pdf.text(`Link: ${linkLines[0] || ''}`, startX, contentY);
        contentY += lineHeight;
        
        // Render remaining lines if any (indented slightly)
        if (linkLines.length > 1) {
          for (let i = 1; i < linkLines.length; i++) {
            pdf.text(linkLines[i], startX + 6, contentY);
            contentY += lineHeight;
          }
        }
      }
      break;
  }

  return contentY;
};

const addHeader = async (pdf: jsPDF, pageWidth: number, margin: number, itinerary?: Tables<'itineraries'>, isFirstPage: boolean = true): Promise<number> => {
  let usedHeight = 0;
  const contentWidth = pageWidth - (margin * 2);

  try {
    // Try to load the Place2Be logo as base64
    const logoBase64 = await loadLogoAsBase64(isFirstPage ? 'large' : 'small');

    if (logoBase64) {
      const logoWidth = isFirstPage ? PDF_CONFIG.LOGO.WIDTH : PDF_CONFIG.LOGO_SMALL.WIDTH;
      const logoHeight = isFirstPage ? PDF_CONFIG.LOGO.HEIGHT : PDF_CONFIG.LOGO_SMALL.HEIGHT;
      const logoY = margin; // top margin

      if (isFirstPage) {
        // Center logo horizontally on first page
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);
      } else {
        // Secondary header on subsequent pages: place logo left-aligned
        const logoX = margin;
        pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);
      }

      usedHeight = logoY + logoHeight;
    } else {
      throw new Error('Logo not loaded');
    }

  } catch (error) {
    // Fallback to text representation
    pdf.setFontSize(PDF_CONFIG.FONT_SIZES.LOGO_TEXT);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
    pdf.setTextColor(...PDF_CONFIG.COLORS.BRAND_PRIMARY);

    const logoText = 'Place2Be';
    const textWidth = pdf.getTextWidth(logoText);
    const textY = margin + 8;

    if (isFirstPage) {
      const textX = (pageWidth - textWidth) / 2;
      pdf.text(logoText, textX, textY);
    } else {
      // left aligned fallback for secondary header
      pdf.text(logoText, margin, textY);
    }

    usedHeight = textY;
  }

  // Insert itinerary cover image under the logo only on the first page
  if (isFirstPage) {
    try {
      const coverUrl = itinerary && (itinerary.image as unknown as string);
      if (coverUrl) {
        const imgData = await loadActivityImageOptimized(coverUrl);
        if (imgData && imgData.base64) {
          // Fit into the available box (max 530x300 px) and preserve aspect ratio (no stretching)
          const pxToMm = (px: number) => (px / 96) * 25.4; // assume 96dpi
          const maxPxWidth = 700;
          const maxPxHeight = 400;

          // Convert px constraints to mm and ensure we don't exceed the content width or page available height
          const maxCoverWidthMM = Math.min(contentWidth, pxToMm(maxPxWidth));
          const pageHeight = pdf.internal.pageSize.getHeight();
          const maxCoverHeightMM = Math.min(pageHeight - margin * 2, pxToMm(maxPxHeight));

          const imgWidthMM = pxToMm(imgData.width || 1);
          const imgHeightMM = pxToMm(imgData.height || 1);

          // scale to fit either width or height limit depending on image aspect ratio
          const scale = Math.min(maxCoverWidthMM / imgWidthMM, maxCoverHeightMM / imgHeightMM);
          const finalWidth = imgWidthMM * scale;
          const finalHeight = imgHeightMM * scale;

          const coverX = (pageWidth - finalWidth) / 2;
          const coverY = usedHeight + 4; // small spacing after logo

          // Draw the cover image centered below the logo
          pdf.addImage(imgData.base64, 'PNG', coverX, coverY, finalWidth, finalHeight);

          usedHeight = coverY + finalHeight;
        }
      }
    } catch (err) {
      console.warn('Could not load itinerary cover image for header:', err);
    }
  }

  // Date/text styling (no separator line)
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL_TEXT);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);

  return usedHeight;
};

const addFooter = (pdf: jsPDF, pageWidth: number, pageHeight: number, margin: number) => {
  const footerY = pageHeight - 20; // More space from bottom

  // // Footer line
  // pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_DARK);
  // pdf.setLineWidth(0.3);
  // pdf.line(margin, footerY - 8, pageWidth - margin, footerY - 8);

  // // Footer text with Place2Be branding
  // pdf.setFontSize(PDF_CONFIG.FONT_SIZES.FOOTER_TEXT);
  // pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  // pdf.setTextColor(...PDF_CONFIG.COLORS.BRAND_PRIMARY);
  // pdf.text('Place2Be - Your Travel Planning Partner', margin, footerY);

  // Page number in gray
  pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_MEDIUM);
  pdf.text(`${pdf.getCurrentPageInfo().pageNumber}`, pageWidth - margin - 20, footerY);
};

// New helper: load remote image and return base64 + pixel dimensions (FULL QUALITY - for logos and covers)
const loadImageUrlAsBase64 = async (url: string): Promise<{ base64: string | null, width: number, height: number } | null> => {
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0);
          const base64 = canvas.toDataURL('image/png');
          resolve({ base64, width: img.naturalWidth, height: img.naturalHeight });
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  } catch (error) {
    console.error('Error loading image as base64:', error);
    return null;
  }
};

// OPTIMIZED loader for activity media images only (reduces PDF size significantly)
const loadActivityImageOptimized = async (url: string): Promise<{ base64: string | null, width: number, height: number } | null> => {
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          
          // OTTIMIZZAZIONE: Ridimensiona l'immagine mantenendo l'aspect ratio
          const MAX_WIDTH = 1200; // Max width in px (buona qualità per PDF)
          const MAX_HEIGHT = 1200; // Max height in px
          
          let width = img.naturalWidth;
          let height = img.naturalHeight;
          
          // Calcola il fattore di scala
          if (width > MAX_WIDTH || height > MAX_HEIGHT) {
            const widthRatio = MAX_WIDTH / width;
            const heightRatio = MAX_HEIGHT / height;
            const scale = Math.min(widthRatio, heightRatio);
            
            width = Math.floor(width * scale);
            height = Math.floor(height * scale);
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          
          // Disegna l'immagine ridimensionata
          ctx.drawImage(img, 0, 0, width, height);
          
          // OTTIMIZZAZIONE: Usa JPEG con qualità 0.85 invece di PNG
          const base64 = canvas.toDataURL('image/jpeg', 0.85);
          
          resolve({ base64, width, height });
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  } catch (error) {
    console.error('Error loading activity image as base64:', error);
    return null;
  }
};

// Function to create icon as base64 using actual Lucide React components
const createIconBase64 = async (type: string): Promise<string> => {
  try {
    // Check cache first
    if (iconCache.has(type)) {
      return iconCache.get(type)!;
    }

    // Create a temporary container for the icon
    const iconContainer = document.createElement('div');
    iconContainer.style.position = 'absolute';
    iconContainer.style.left = '-9999px';
    iconContainer.style.top = '-9999px';
    iconContainer.style.width = '16px';
    iconContainer.style.height = '16px';
    iconContainer.style.display = 'flex';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.justifyContent = 'center';
    iconContainer.style.backgroundColor = 'transparent';

    document.body.appendChild(iconContainer);

    // Get the actual Lucide React component
    const IconComponent = getIconComponent(type);

    // Render the actual Lucide React component
    const iconElement = document.createElement('div');
    iconContainer.appendChild(iconElement);

    // Use ReactDOM to render the actual Lucide component with createRoot (React 18)
    const { createRoot } = await import('react-dom/client');
    const root = createRoot(iconElement);
    root.render(React.createElement(IconComponent, { width: 16, height: 16, color: '#000000' }));

    // Wait for React to render the component before capturing
    await new Promise(resolve => setTimeout(resolve, 100));

    // Use html2canvas to convert to image
    const canvas = await html2canvas(iconContainer, {
      backgroundColor: null,
      width: 16,
      height: 16,
      scale: 2 // Higher resolution
    });

    const imgData = canvas.toDataURL('image/png');

    // Clean up
    document.body.removeChild(iconContainer);

    // Cache the result
    iconCache.set(type, imgData);

    return imgData;
  } catch (error) {
    console.error('Error creating icon:', error);
    return '';
  }
};

// Replaced implementation: try loadImageUrlAsBase64, then fetch SVG and convert to data URL and draw on canvas
const loadLogoAsBase64 = async (size: 'small' | 'large'): Promise<string | null> => {
  try {
    // 1) Try the generic loader first
    const imgData = await loadImageUrlAsBase64(size === 'large' ? PDF_CONFIG.LOGO.URL : PDF_CONFIG.LOGO_SMALL.URL);
    if (imgData && imgData.base64) return imgData.base64;

    // 2) Fallback: fetch the SVG/asset text and convert to a data URL (works better for inline/local SVG)
    const resp = await fetch(size === 'large' ? PDF_CONFIG.LOGO.URL : PDF_CONFIG.LOGO_SMALL.URL);
    if (!resp.ok) return null;
    const svgText = await resp.text();
    const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText);

    // 3) Draw the SVG data URL onto a canvas with configured high-res size, then export PNG base64
    const canvas = document.createElement('canvas');
    canvas.width = size === 'large' ? PDF_CONFIG.LOGO.CANVAS_WIDTH : PDF_CONFIG.LOGO_SMALL.CANVAS_WIDTH;
    canvas.height = size === 'large' ? PDF_CONFIG.LOGO.CANVAS_HEIGHT : PDF_CONFIG.LOGO_SMALL.CANVAS_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    return await new Promise((resolve) => {
      img.onload = () => {
        try {
          // Clear and draw scaled SVG onto canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const base64 = canvas.toDataURL('image/png');
          resolve(base64);
        } catch (err) {
          console.error('Error drawing SVG to canvas', err);
          resolve(null);
        }
      };
      img.onerror = (e) => {
        console.warn('Failed to load SVG as image for logo fallback', e);
        resolve(null);
      };
      img.src = svgDataUrl;
    });
  } catch (error) {
    console.error('Error loading logo as base64:', error);
    return null;
  }
};
